import { Card, CardContent } from "../ui/card";
import { Switch } from "../ui/switch";
import { IoNotificationsOutline, IoMailOutline, IoWarningOutline } from "react-icons/io5";
import { useGetUserPreferences, useUpdateUserPreferences } from "@/hooks/useUserPreferences";
import type { UserPreferences } from "@/types/UserPreferences";
import { Spinner } from "../Spinner";
import toast from "react-hot-toast";

export type NotificationSetting = {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  key: "notifyStatusUpdates" | "notifyCityNews" | "notifyGovernmentReplies" | "notifyNeighborhoodNews";
};

interface NotificationMenuProps {
  title?: string;
}

const notificationSettings: NotificationSetting[] = [
  {
    id: "status-updates",
    icon: <IoNotificationsOutline className="h-5 w-5 text-gray-500" />,
    title: "Atualizações das minhas ocorrências",
    description: "Receba notificações sobre suas ocorrências",
    key: "notifyStatusUpdates",
  },
  {
    id: "government-replies",
    icon: <IoMailOutline className="h-5 w-5 text-gray-500" />,
    title: "Respostas da prefeitura",
    description: "Notificações de respostas oficiais",
    key: "notifyGovernmentReplies",
  },
  {
    id: "city-announcements",
    icon: <IoMailOutline className="h-5 w-5 text-gray-500" />,
    title: "Comunicados gerais da cidade",
    description: "Avisos e alertas importantes",
    key: "notifyCityNews",
  },
  {
    id: "neighborhood-news",
    icon: <IoNotificationsOutline className="h-5 w-5 text-gray-500" />,
    title: "Comunicados do meu bairro",
    description: "Notícias e avisos do seu bairro",
    key: "notifyNeighborhoodNews",
  },
];

export const NotificationMenu = ({
  title = "Notificações",
}: NotificationMenuProps) => {
  const { data: preferences, isLoading } = useGetUserPreferences();
  const updatePreferences = useUpdateUserPreferences();
  
  // Verificar se todas as notificações estão desabilitadas
  const allDisabled = !preferences?.notifyStatusUpdates && 
                      !preferences?.notifyCityNews && 
                      !preferences?.notifyGovernmentReplies && 
                      !preferences?.notifyNeighborhoodNews;

  const handleToggle = async (key: keyof UserPreferences, checked: boolean) => {
    try {
      await updatePreferences.mutateAsync({
        [key]: checked,
      });
      toast.success("Preferência atualizada!");
    } catch (error) {
      console.error("Erro ao atualizar preferência:", error);
      toast.error("Erro ao salvar. Tente novamente.");
    }
  };

  if (isLoading) {
    return (
      <div className="mt-5">
        <h3 className="mb-2 font-semibold text-[17px]">{title}</h3>
        <Card>
          <CardContent className="p-8 flex items-center justify-center">
            <Spinner />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mt-5">
      <h3 className="mb-2 font-semibold text-[17px]">{title}</h3>
      
      {allDisabled && (
        <Card className="mb-4 border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <IoWarningOutline className="text-orange-600 text-2xl flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-orange-900 mb-1">Atenção: Todas as notificações estão desabilitadas</h4>
                <p className="text-sm text-orange-800">
                  Você não receberá mais notificações em tempo real sobre suas ocorrências ou comunicados da cidade.
                  Você ainda poderá ver notificações antigas na tela de alertas.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <Card>
        <CardContent className="divide-y bg-white rounded-lg divide-gray-200 p-0">
          {notificationSettings.map((setting) => {
            const { key: settingKey, ...restProps } = setting;
            return (
              <NotificationMenuItem
                key={setting.id}
                {...restProps}
                checked={preferences?.[settingKey] ?? false}
                disabled={updatePreferences.isPending}
                onToggle={(checked) => handleToggle(settingKey, checked)}
              />
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

interface NotificationMenuItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onToggle?: (checked: boolean) => void;
}

const NotificationMenuItem = ({
  icon,
  title,
  description,
  checked,
  disabled = false,
  onToggle,
}: NotificationMenuItemProps) => {
  return (
    <div className="flex items-center justify-between p-4">
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <p className="font-medium text-gray-900">{title}</p>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
      <Switch 
        checked={checked} 
        disabled={disabled}
        onCheckedChange={onToggle} 
      />
    </div>
  );
};
